import { useState } from 'react'

import { IEntity, INLUDataExample } from '../../../../stores/nluDataStore'

import { joinClassNames, stringToColor } from '../../../../utils/styles'
import styles from './message.module.scss'

interface ICoordinates {
    x: number
    y: number
}

interface IMessageProps {
    example: INLUDataExample
    handleStartEditingEntities: (wordObject: IEntity) => void
    editingEntities: IEntity[]
    examplesRef: React.RefObject<HTMLDivElement>
    messageRef: React.RefObject<HTMLDivElement>
    allEntities: string[]
}

function Message(props: IMessageProps) {
    const {
        example,
        handleStartEditingEntities,
        editingEntities,
        examplesRef,
        messageRef,
        allEntities,
    } = props

    const { text, tokens } = example

    const [defaultEntity] = allEntities

    const [startCoordinates, setStartCoordinates] = useState<ICoordinates>()
    const [checkedWords, setCheckedWords] = useState<number[]>([])

    // The coordinates must be relative, through the scroll appearing in the div, not on the page
    const getRelativeCoordinates = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
        if (examplesRef.current) {
            return {
                x: e.pageX - examplesRef.current.scrollLeft,
                y: e.pageY - examplesRef.current.scrollTop,
            }
        }
    }

    const handleMouseDown = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
        setStartCoordinates(getRelativeCoordinates(e))
    }

    const areCoordinatesBetween = (
        coordinate: ICoordinates,
        childCoordinate: ICoordinates,
        xMeasurementError = 0,
        yMeasurementError = 0
    ) => {
        const isXBetween =
            childCoordinate.x - xMeasurementError < coordinate.x &&
            coordinate.x < childCoordinate.x + xMeasurementError
        const isYBetween =
            childCoordinate.y - yMeasurementError < coordinate.y &&
            coordinate.y < childCoordinate.y + yMeasurementError

        return isXBetween && isYBetween
    }

    const getChildrenBetween = (
        startCoordinates: ICoordinates,
        endCoordinates: ICoordinates
    ) => {
        const childNodes = messageRef.current
            ?.childNodes as NodeListOf<HTMLDivElement>

        let firstChildSelectedIndex = -1
        let lastChildSelectedIndex = -1

        childNodes.forEach((child, index) => {
            if (!examplesRef.current) return
            const rect = child.getBoundingClientRect()
            const childCoordinates = {
                x:
                    rect.left -
                    examplesRef.current.scrollLeft +
                    child.offsetWidth / 2,
                y:
                    rect.top -
                    examplesRef.current.scrollTop +
                    child.offsetHeight / 2,
            }

            const xMeasurementError = child.offsetWidth / 2
            const yMeasurementError = child.offsetHeight / 2

            const isFirstChild = areCoordinatesBetween(
                startCoordinates,
                childCoordinates,
                xMeasurementError,
                yMeasurementError
            )

            if (isFirstChild) {
                firstChildSelectedIndex = index
            }

            const isLastChild = areCoordinatesBetween(
                endCoordinates,
                childCoordinates,
                xMeasurementError,
                yMeasurementError
            )

            if (isLastChild) {
                lastChildSelectedIndex = index
            }
        })

        if (firstChildSelectedIndex === -1 || lastChildSelectedIndex === -1) {
            return
        }

        const [previousChild, laterChild] = [
            firstChildSelectedIndex,
            lastChildSelectedIndex,
        ].sort((a, b) => a - b)

        return Array.from(Array(laterChild + 1).keys()).slice(
            previousChild,
            laterChild + 1
        )
    }

    const areCoordinatesUndefined = (coordinates: ICoordinates) => {
        return coordinates.x === undefined || coordinates.y === undefined
    }

    const handleMouseMove = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
        if (!examplesRef.current) return

        const endCoordinates = getRelativeCoordinates(e)

        if (
            !endCoordinates ||
            !startCoordinates ||
            areCoordinatesUndefined(startCoordinates) ||
            areCoordinatesUndefined(endCoordinates)
        ) {
            return
        }

        const childrenBetween = getChildrenBetween(
            startCoordinates,
            endCoordinates
        )

        childrenBetween && setCheckedWords(childrenBetween)
    }

    const handleMouseUp = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setCheckedWords([])

        if (!examplesRef.current) return

        const endCoordinates = getRelativeCoordinates(e)

        if (
            !endCoordinates ||
            !startCoordinates ||
            areCoordinatesUndefined(startCoordinates) ||
            areCoordinatesUndefined(endCoordinates)
        ) {
            return
        }

        const childrenBetween = getChildrenBetween(
            startCoordinates,
            endCoordinates
        )

        childrenBetween && addNewEntityByDrag(childrenBetween)
        setStartCoordinates(undefined)
    }

    const handleMouseLeave = () => {
        setStartCoordinates(undefined)
        setCheckedWords([])
    }

    const addNewEntityByDrag = (childrenBetween: number[]) => {
        if (childrenBetween.length === 0 || !tokens.length) {
            return
        }

        const isEntityCollision = editingEntities.some((entity) =>
            childrenBetween.some((child) => entity.tokens.includes(child))
        )

        if (isEntityCollision) return

        const start = tokens[childrenBetween[0]].start
        const end = tokens[childrenBetween[childrenBetween.length - 1]].end
        const value = text.slice(start, end)

        const newEntity: IEntity = {
            start,
            end,
            value,
            tokens: childrenBetween,
            entity: defaultEntity,
        }

        handleStartEditingEntities(newEntity)
        setStartCoordinates(undefined)
    }

    const entityClassName = (index: number, entity?: IEntity): string =>
        joinClassNames(
            styles.message__word,
            checkedWords?.includes(index) && styles.message__wordChecked,
            entity && styles.message__wordActive,
            entity &&
                Math.min(...entity.tokens) === index &&
                styles.message__firstEntityToken,
            entity &&
                Math.max(...entity.tokens) === index &&
                styles.message__lastEntityToken
        )

    const getEntityForToken = (index: number) => {
        return editingEntities.find((entity) => entity.tokens.includes(index))
    }

    const wordInLineStyle = (string?: string) => {
        if (!string) return

        const colors = stringToColor(string)

        return {
            backgroundColor: colors.background,
            color: colors.font,
        }
    }

    return (
        <div
            className={styles.message}
            ref={messageRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
        >
            {tokens?.map((token, index) => {
                const entityForToken = getEntityForToken(index)
                return (
                    <button
                        className={entityClassName(index, entityForToken)}
                        style={wordInLineStyle(entityForToken?.entity)}
                        onClick={() => {
                            handleStartEditingEntities(
                                entityForToken
                                    ? entityForToken
                                    : {
                                          ...token,
                                          tokens: [index],
                                          entity: defaultEntity,
                                      }
                            )
                            setStartCoordinates(undefined)
                        }}
                        key={`${token.value}-${index}`}
                    >
                        {token.value}
                    </button>
                )
            })}
        </div>
    )
}

export default Message
